import React from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import ActionButton from '../../../components/shared/Buttons/ActionButton.container';
import { useNavigation } from '@react-navigation/native';
import colors from '../../../config/colors';
import Typography from '../../../components/shared/Typography/Typography.component';
import ImageIcon from '../../../components/shared/Icon/ImageIcon.component';
import I18n from '../../../components/shared/i18n/I18n.component';
import { MonthAttendanceResponse } from '../../../services/hooks/useMonthAttendance.hook';
import { useSelector } from 'react-redux';
import { getChosenDate } from '../../../store/selectors/clockReport.selector';


type StatItemProps = {
	label: string;
	value: string;
	outOf?: string;
	isMobile: boolean;
};

type AttendanceStatsCardProps = {
	showButton?: boolean;
	monthlyAttendanceData: MonthAttendanceResponse | null;
};

const hebrewMonths = [
		'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
		'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר',
	];


const getDateLabel = (attendanceData: MonthAttendanceResponse | null, fallbackDate?: string | null) => {
	if (!attendanceData?.monthlyView) {
		// in case the API response doesn't include the monthlyView data, we can still show the month and year based on the chosen date (fallbackDate)
		if (fallbackDate) {
			const [year, month] = fallbackDate.split('-').map(Number);
			return `${hebrewMonths[month - 1]} ${year}`;
		}
		return '';
	}
	const month = attendanceData.monthlyView.month ?? 0;
	const year = attendanceData.monthlyView.year ?? 0;
	return `${hebrewMonths[month - 1]} ${year}`;
}

const getHourLabel = (minutes: number) : string => {
	const hours = Math.floor(minutes / 60);
	const remainingMinutes = minutes % 60;
	return `${hours}:${remainingMinutes.toString().padStart(2, '0')}`;
}

const StatItem = ({ label, value, outOf, isMobile }: StatItemProps) => (
	<View style={[styles.statItem, Platform.select({ ios: { marginEnd: 12, marginLeft: 6 }})]}>
		<Typography size={isMobile ? 11 : 12} font='medium' color='textGrey' style={{ letterSpacing: 0.4, textAlign: 'right', ...Platform.select({ web: {}, default: { marginBottom: 8 } }) }}>
			{label}
		</Typography>
		<View style={styles.statValueRow}>
			<Typography size={isMobile ? 14 : 18} weight='400' color='primaryDarkBlue' style={Platform.select({ web: {}, default: { marginEnd: 2 } })}>
				{value}
			</Typography>
			{outOf ? (
				<Typography size={isMobile ? 12 : 14} weight='400' color='primaryDarkBlue'>
					{`/${outOf}`}
				</Typography>
			) : null}
		</View>
	</View>
);

const AttendanceStatsCard = ({showButton = true, monthlyAttendanceData}: AttendanceStatsCardProps) => {
	const navigation = useNavigation();
	const chosenDate = useSelector(getChosenDate);
	const isMobile = Platform.OS !== 'web';

	return (
		<View style={styles.card}>
			<View style={styles.header}>


				<View style={styles.titleRow}>
					<Typography size={14} weight='400' color='greyText' style={Platform.select({ native: { marginEnd: 8 } })}>
						{getDateLabel(monthlyAttendanceData, chosenDate)}
					</Typography>
					<I18n size={14} font='medium' color='primaryDarkBlue'>
						reportView.attendanceStatsCard.title
					</I18n>
				</View>

				{!isMobile && showButton && (
					<ActionButton
						onPress={() =>
							navigation.navigate('calendarAttendanceReports')
						}
					>
						<ImageIcon name={'chevronLeft'} />
						<I18n size={14} weight='normal' color='primary'>
							reportView.wageDataSummary.redirectToPreviouse
						</I18n>
					</ActionButton>
				)}

	
			</View> 

			<View style={styles.statsRow}>
				<StatItem 
					label='אירועים שדווחו' 
					value={monthlyAttendanceData?.eventsCount?.toString() ?? '0'} 
					isMobile={isMobile} />
				<View style={[styles.verticalDivider, Platform.OS === 'ios' && { marginRight: 4 }]} />
				<StatItem
					label='ימי עבודה'
					value={monthlyAttendanceData?.monthlyView?.workLogDays?.toString() ?? '0'}
					outOf={monthlyAttendanceData?.monthlyView?.standardDays?.toString() ?? '0'}
					isMobile={isMobile}
				/>
				<View style={[styles.verticalDivider, Platform.OS === 'ios' && { marginRight: 4 }]} />
				<StatItem
					label='שעות החודש'
					value={monthlyAttendanceData?.monthlyView?.workLogMinutes ? getHourLabel(monthlyAttendanceData.monthlyView.workLogMinutes) : '00:00'}
					outOf={monthlyAttendanceData?.monthlyView?.standardMinutes 
								? `${(monthlyAttendanceData.monthlyView.standardMinutes / 60)}` 
								: '0'}
					isMobile={isMobile}				
				/>
			</View>

							
			{isMobile && showButton && (
				<View style={{ display: 'flex', flexDirection: 'row-reverse', alignItems: 'flex-end'}}>
					<ActionButton
						onPress={() =>
							navigation.navigate('calendarAttendanceReports')
						}
					>
						<ImageIcon name={'chevronLeft'} />
						<I18n size={14} weight='normal' color='primary'>
							reportView.wageDataSummary.redirectToPreviouse
						</I18n>
					</ActionButton>
				</View>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	card: {
		width: '100%',
		backgroundColor: colors.white,
		borderRadius: 8,
		padding: 24,
		...Platform.select({
			web: { gap: 16, boxShadow: '2px 2px 16px 0 rgba(0, 0, 0, 0.04)' },
			ios: { shadowColor: '#000', shadowOffset: { width: 2, height: 2 }, shadowOpacity: 0.04, shadowRadius: 16 },
			android: { elevation: 2 },
		}),
	},
	header: {
		flexDirection: 'row-reverse',
		justifyContent: 'space-between',
		alignItems: 'center',
		...Platform.select({ web: { gap: 8 }, native: { marginBottom: 16 } }),
	},
	titleRow: {
		flexDirection: 'row',
		alignItems: 'center',
		...Platform.select({ web: { gap: 8 }, default: {flexShrink: 1} }),
	},
	divider: {
		height: 1,
		backgroundColor: colors.divider,
	},
	statsRow: {
		width: '100%',
		flexDirection: 'row',
		justifyContent: 'flex-end',
		alignItems: 'center',
		...Platform.select({ web: { gap: 32 }, ios: { marginBottom: 8 }, default: { gap: 12 } }),
	},
	statItem: {
		flexDirection: 'column',
		alignItems: 'stretch',
		flex: 1,
		...Platform.select({ web: { gap: 8 }, default: {} }),
	},
	statValueRow: {
		flexDirection: 'row',
		alignItems: 'flex-end',
		justifyContent: 'flex-end',
		...Platform.select({ web: { gap: 2 }, default: {} }),
	},
	verticalDivider: {
		width: 1,
		height: 40,
		backgroundColor: colors.divider,
	},
});

export default AttendanceStatsCard;
